/**
 * 문의 길잡이 — 분류 → 세부 → 자주 묻는 질문 → 레이 → (조건) 상담사 연결 (2026-10-28)
 * 기획 docs/ops/ops-chat-messenger-plan.md §1-2 · §5-7 · 마이그레이션 20261207100000
 *
 * 🔒 규칙
 *   · 상담사 연결 여부는 서버(support_connect_reason)가 정한다. 아래 connectReason() 은 버튼을 보여 줄지 정하는
 *     화면용 거울일 뿐이다 — 두 곳의 규칙이 같아야 한다(supportGuide.test.js 가 서버 소스와 대조).
 *   · 길 기록(support_path_log)의 kind·reason 문자열은 서버 check 제약과 같아야 한다.
 *   · 레이 답 요약(lay summary)은 그 사람이 이 화면에서 레이와 나눈 대화뿐이다(서버가 10개·글자 수로 자른다).
 */
import { supabase } from "./supabaseClient";

/** 서버 support_path_event.kind check 와 같은 목록 */
export const PATH_KINDS = Object.freeze([
  "topic", "subtopic", "skip_topic", "faq_open", "faq_yes", "faq_no", "ask_lay",
  "lay_answer", "lay_unknown", "lay_error", "lay_yes", "lay_not_yet",
]);
/** 「아직 해결 안 됐어요」 이유 — 서버 reason check 와 같은 목록 */
export const NOT_YET_REASONS = Object.freeze(["account", "wrong", "unclear"]);

export function errorCodeOf(err) {
  const msg = String(err?.message || err?.details || "");
  const m = /(not_allowed|path_used|rate_limited|not_found|forbidden|disabled|no_member|bad_topic|bad_kind|reason_required|too_long|bad_summary)/.exec(msg);
  return m ? m[1] : "unknown";
}

export async function fetchTopicTree() {
  const { data, error } = await supabase.rpc("support_topic_tree");
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function fetchTopicFaq(topicKey) {
  const { data, error } = await supabase.rpc("support_topic_faq", { p_topic: topicKey });
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

export async function startPath() {
  const { data, error } = await supabase.rpc("support_path_start");
  if (error) throw error;
  return data;
}

/** 길 기록 — 실패해도 화면을 멈추지 않는다(연결 판정에 필요한 기록만 다시 시도할 가치가 있다) */
export async function logPath(pathId, kind, { topic = null, kbEntryId = null, reason = null, layRef = null } = {}) {
  if (!pathId || !PATH_KINDS.includes(kind)) return false;
  const { error } = await supabase.rpc("support_path_log", {
    p_path_id: pathId, p_kind: kind, p_topic: topic, p_kb_entry_id: kbEntryId, p_reason: reason, p_lay_ref: layRef,
  });
  if (error) throw error;
  return true;
}

export async function connectToAgent(pathId, note, laySummary) {
  const { data, error } = await supabase.rpc("ops_chat_connect", {
    p_path_id: pathId, p_note: note || null, p_lay_summary: laySummary && laySummary.length ? laySummary : null,
  });
  if (error) throw error;
  return data;
}

/**
 * 화면용 거울 — 서버 support_connect_reason 과 같은 순서·조건.
 * events: [{ kind, topicIsUrgent?, reason? }]  → "urgent" | "lay_down" | "lay" | null
 */
export function connectReason(events) {
  const list = Array.isArray(events) ? events : [];
  const has = (k) => list.some((e) => e.kind === k);
  const count = (k) => list.filter((e) => e.kind === k).length;
  if (list.some((e) => e.kind === "subtopic" && e.topicIsUrgent)) return "urgent";
  if (has("lay_error")) return "lay_down";
  if (has("lay_unknown")) return "lay";
  if (list.some((e) => e.kind === "lay_not_yet" && e.reason === "account") && has("lay_answer")) return "lay";
  if (count("lay_not_yet") >= 2 && count("lay_answer") >= 2) return "lay";
  return null;
}

/** 레이 대화(화면 메시지 목록) → 서버로 넘길 요약 [{ q, a, unknown }] (최근 10쌍) */
export function buildLaySummary(messages) {
  const out = [];
  const list = Array.isArray(messages) ? messages : [];
  for (let i = 0; i < list.length; i += 1) {
    const m = list[i];
    if (m.role !== "user") continue;
    const next = list[i + 1];
    const a = next && next.role === "lay" ? next : null;
    out.push({ q: String(m.content || "").slice(0, 1000), a: a ? String(a.content || "").slice(0, 2000) : "", unknown: Boolean(a?.unknown) });
  }
  return out.slice(-10);
}

/** 레이에게 넘길 화면 맥락 — 고른 분류·세부(데이터로만 쓰인다) */
export function topicContext(category, sub) {
  const parts = [category?.label, sub?.label].filter(Boolean);
  return parts.length ? `문의 분류: ${parts.join(" › ")}` : "";
}

/** 연결된 방의 시스템 줄·상태를 화면 문장으로 */
export function sessionLabel(room) {
  if (!room) return "";
  if (room.status === "closed") return "상담 끝";
  if (room.session_status === "waiting") return "연결 대기";
  return "상담 중";
}
